import {Action, Selector, State, StateContext, Store} from '@ngxs/store';
import {HttpClient} from '@angular/common/http';
import {catchError, tap} from 'rxjs/operators';
import {Injectable} from '@angular/core';
import {Observable, of} from 'rxjs';
import {endpoints} from '../../constants/endpoints';
import { PDFDocument } from 'pdf-lib';
import * as Act from './settings.actions';
import { IDrugOfInterest } from 'src/app/settings/interfaces/IDrugOfInterest';
import { AddOrUpdateLocation, MarkLocationAsIrremovable, RemoveLocation, UpdateLocationActivity } from '../inventory/inventory.actions';
import { UpdateActivityForInventoryLocation } from './settings.actions';

export interface SettingsStateModel {
  loading: boolean;
  success: boolean;
  drugsOfInterest: IDrugOfInterest[];
  cannotRemoveInventoryLocationAsLocationIsNotEmpty: boolean;
  cannotDeactivateAsLocationIsNotEmpty: boolean;
}

@State<SettingsStateModel>({
  name: 'settings',
})

@Injectable()
export class SettingsState {

  constructor(
    private http: HttpClient,
    private store: Store,
  ) {
  }

  @Selector()
  static loading(state: SettingsStateModel): boolean {
    return state.loading;
  }

  @Selector()
  static success(state: SettingsStateModel): boolean {
    return state.success;
  }

  @Selector()
  static drugsOfInterest(state: SettingsStateModel): IDrugOfInterest[] {
    return state.drugsOfInterest;
  }


  @Action(Act.CreatePioneerRxIntegration)
  savePioneerRxPopulatedConsentForm(ctx: StateContext<SettingsStateModel>, {payload}: Act.CreatePioneerRxIntegration): Observable<any> {
    ctx.patchState({loading: true});

    return this.http.put(endpoints.integration.pioneerRx.createIntegration, payload)
    .pipe(
      tap((responseData: any) => {
        ctx.patchState({
          loading: false,
          success: responseData?.success
        });
      }),
      catchError((err) => {
        ctx.patchState({loading: false});
        return of(null);
      }),
    );
  }

  @Action(Act.GetDrugsOfInterest)
  getDrugsOfInterest(ctx: StateContext<SettingsStateModel>, {type}: Act.GetDrugsOfInterest): Observable<any> {
 
    ctx.patchState({loading: true});
    var url = endpoints.preference.getDrugsOfInterest;
    if (type) {
      url += type;
    }
    return this.http.get(endpoints.preference.getDrugsOfInterest)
      .pipe(
        tap((resp: any) => {
          ctx.patchState({
            drugsOfInterest: resp?.value,
            loading: false,
          });
        }),
        catchError((err) => {
          ctx.patchState({loading: false});
          return of(null);
        }),
      );
  }

  @Action(Act.SaveInventoryLocation)
  saveInventoryLocation(ctx: StateContext<SettingsStateModel>, {payload}: Act.SaveInventoryLocation): Observable<any> {
    ctx.patchState({loading: true});

    return this.http.post(endpoints.inventoryLocation.saveLocation, payload)
      .pipe(
        tap((resp: any) => {
          if (resp.success) {
             this.store.dispatch(new AddOrUpdateLocation(resp?.value));
          }
          ctx.patchState({
            loading: false,
          });

        }),
        catchError((err) => {
          ctx.patchState({loading: false});
          return of(null);
        }),
      );
  }

  @Action(Act.RemoveInventoryLocation)
  removeInventoryLocation(ctx: StateContext<SettingsStateModel>, {id}: Act.RemoveInventoryLocation): Observable<void> {
    ctx.patchState({loading: true, cannotRemoveInventoryLocationAsLocationIsNotEmpty: null});

    return this.http.delete(endpoints.inventoryLocation.removeLocation + id)
      .pipe(
        tap((resp: any) => {
          if (resp.success && !resp.value?.cannotRemoveAsLocationIsNotEmpty) {
             this.store.dispatch(new RemoveLocation(id));
          }
          if (resp.success && resp.value?.cannotRemoveAsLocationIsNotEmpty) {
            ctx.patchState({
              cannotRemoveInventoryLocationAsLocationIsNotEmpty: true,
            });
            this.store.dispatch(new MarkLocationAsIrremovable(id));
          }
          ctx.patchState({
            loading: false,
          });

        }),
        catchError((err) => {
          ctx.patchState({loading: false});
          return of(null);
        }),
      );
  }

  @Action(Act.UpdateActivityForInventoryLocation)
  updateActivityForInventoryLocation(ctx: StateContext<SettingsStateModel>, {payload}: Act.UpdateActivityForInventoryLocation): Observable<void> {
    ctx.patchState({loading: true, cannotDeactivateAsLocationIsNotEmpty: null});

    return this.http.patch(endpoints.inventoryLocation.updateActivityStatus, payload)
      .pipe(
        tap((resp: any) => {
          if (resp.success && !resp.value?.cannotDeactivateAsInventoriesAreNotEmpty) {
             this.store.dispatch(new UpdateLocationActivity(payload?.locationId, payload?.isActive));
          }
          if (resp.success && resp.value?.cannotDeactivateAsInventoriesAreNotEmpty) {
            ctx.patchState({
              cannotDeactivateAsLocationIsNotEmpty: true,
            });
          }
          ctx.patchState({
            loading: false,
          });

        }),
        catchError((err) => {
          ctx.patchState({loading: false});
          return of(null);
        }),
      );
  }

  @Action(Act.InitiateFrameworkLtcIntegration)
  initiateFrameworkLtcIntegration(ctx: StateContext<SettingsStateModel>, {pin}: Act.InitiateFrameworkLtcIntegration): Observable<any> {
    ctx.patchState({loading: true});

    return this.http.put(endpoints.integration.frameworkLTC.createIntegration, {pin})
    .pipe(
      tap((responseData: any) => {
        ctx.patchState({
          loading: false,
          success: responseData?.success
        });
      }),
      catchError((err) => {
        ctx.patchState({loading: false});
        return of(null);
      }),
    );
  }
}
